import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { SiLinear } from "@icons-pack/react-simple-icons";
import { ChevronDown, ChevronRight, ChevronUp } from "lucide-react";
import { Button } from "@repo/blacksmith-ui/button";
import { cn } from "@repo/blacksmith-ui/cn";
import { SessionMarkdown } from "./LazySessionMarkdown";
export interface LinearComment {
  author?: string;
  body: string;
}
export interface ParsedLinearPrompt {
  identifier: string;
  title: string;
  description?: string;
  teamName?: string;
  projectName?: string;
  labels: string[];
  /** The comment that mentioned the agent; absent when the issue was delegated. */
  directive?: LinearComment;
  /** Context comments, excluding the directive. */
  comments: LinearComment[];
}

/**
 * Decode XML entities and tidy up trailing whitespace. Preserves line
 * breaks so markdown content (headings, lists, fenced code) survives
 * round-tripping through the parser.
 */
function decodeXmlText(value: string): string {
  return value.replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&amp;/g, "&").replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
}

/**
 * Collapse a single-line value (title, label, author). All whitespace,
 * including newlines, becomes a single space. Use for short attribute-like
 * fields, never for description or comment bodies (which are markdown).
 */
function flattenInline(value: string): string {
  return decodeXmlText(value.replace(/<[^>]+>/g, " ").replace(/\s+/g, " "));
}

/**
 * Pull a multi-line block out of an XML wrapper without flattening its
 * internal whitespace. The block is expected to be markdown (Linear sends
 * issue descriptions and comment bodies as markdown inside the prompt
 * context XML), so we strip only the outermost tag and keep newlines and
 * inline markdown intact.
 */
function extractMarkdownBlock(content: string, tag: string): string | undefined {
  const match = content.match(new RegExp(`<${tag}[^>]*>([\\s\\S]*?)</${tag}>`));
  if (!match?.[1]) return undefined;
  const inner = decodeXmlText(match[1]);
  return inner.length > 0 ? inner : undefined;
}
function extractInlineTag(content: string, tag: string): string | undefined {
  const match = content.match(new RegExp(`<${tag}[^>]*>([\\s\\S]*?)</${tag}>`));
  return match?.[1] ? flattenInline(match[1]) : undefined;
}
function extractAttribute(content: string, tag: string, attribute: string): string | undefined {
  const match = content.match(new RegExp(`<${tag}[^>]*\\s${attribute}="([^"]*)"[^>]*>`));
  return match?.[1] ? decodeXmlText(match[1]) : undefined;
}
function extractAllInlineTags(content: string, tag: string): string[] {
  return Array.from(content.matchAll(new RegExp(`<${tag}[^>]*>([\\s\\S]*?)</${tag}>`, "g"))).map(match => match[1] ? flattenInline(match[1]) : "").filter(Boolean);
}

/**
 * Rewrite Linear-specific mention markup into plain markdown so
 * SessionMarkdown renders readable `@name` tags instead of literal XML.
 *
 * Linear's promptContext example
 * (https://linear.app/developers/agent-interaction) shows that
 * user mentions inside descriptions and comments arrive as
 * `<user id="<uuid>">display-name</user>` XML wrappers:
 *
 *     <comment author="John Doe" created-at="...">
 *       <user id="df3fc33e-...">botcoder</user> Please implement this
 *     </comment>
 *
 * SessionMarkdown's `escapeAngleBrackets` step strips any tag that isn't
 * in `SAFE_HTML_TAGS`, so without preprocessing the user would see a
 * literal `<user id="...">botcoder</user>` token in the preview. Strip
 * the wrapper and prefix the inner display-name with `@`.
 *
 * Also handle the bare-URL form documented in
 * https://linear.app/developers/graphql ("mentions can be created in
 * Markdown by using the plain URL of the resource"). That's the format
 * agents send back to Linear in Agent Activity content, and it can also
 * appear in descriptions copy-pasted from Markdown outside the editor.
 * Convert profile URLs to `[@<slug>](url)` and issue URLs to
 * `[<issue-id>](url)`. URLs already wrapped in markdown link `[text](url)`,
 * autolink `<url>`, or image `![alt](url)` syntax are left alone via the
 * negative lookbehind on `([<` so we never double-wrap.
 */
export function normalizeLinearMentions(text: string): string {
  // <user id="...">display-name</user> -> @display-name.
  // The `id` attribute is dropped because we don't surface user IDs in
  // the preview; the display-name alone is what a human recognizes.
  let result = text.replace(/<user\b[^>]*>([\s\S]*?)<\/user>/g, (_, name: string) => {
    const trimmed = name.trim();
    return trimmed ? `@${trimmed}` : "";
  });

  // <issue id="..." href="https://linear.app/.../BLA-1">BLA-1</issue> -> [BLA-1](href).
  result = result.replace(/<issue\b([^>]*)>([\s\S]*?)<\/issue>/g, (_, attributes: string, label: string) => {
    const trimmed = label.trim();
    const href = attributes.match(/\bhref="([^"]*)"/)?.[1];
    return href && trimmed ? `[${trimmed}](${href})` : trimmed;
  });

  // Bare profile URLs are matched only when not already inside another
  // markdown construct. The slug character class deliberately omits `.`
  // so a trailing sentence period like ".../profiles/john." doesn't get
  // pulled into the link href (Linear slugs are typically kebab-case or
  // use underscores; dotted slugs are rare and not worth the false
  // positives).
  const profilePattern = /(?<![([<])https:\/\/linear\.app\/[^/\s]+\/profiles\/([A-Za-z0-9_-]+)/g;
  // Same shape for issue mentions: `.../issue/<ID>[/<slug>]`. The id is
  // an uppercase team prefix + `-` + digits. The optional `/slug` tail
  // (Linear's pretty-URL component) is consumed so the full URL lands in
  // the link href, but it's restricted to slug-like chars so trailing
  // punctuation is left outside the match.
  const issuePattern = /(?<![([<])https:\/\/linear\.app\/[^/\s]+\/issue\/([A-Z][A-Z0-9]*-\d+)(?:\/[A-Za-z0-9_-]+)?/g;
  return result.replace(profilePattern, (url, slug) => `[@${slug}](${url})`).replace(issuePattern, (url, issueId) => `[${issueId}](${url})`);
}
function extractComments(content: string): LinearComment[] {
  return Array.from(content.matchAll(/<comment\b([^>]*)>([\s\S]*?)<\/comment>/g)).map(match => {
    const author = match[1]?.match(/\bauthor="([^"]*)"/)?.[1];
    return {
      author: author ? flattenInline(author) : undefined,
      body: match[2] ? normalizeLinearMentions(decodeXmlText(match[2])) : ""
    };
  }).filter(comment => comment.body.length > 0);
}
const DIRECTIVE_THREAD_PATTERN = /<primary-directive-thread\b[^>]*>([\s\S]*?)<\/primary-directive-thread>/;
export function parseLinearPromptContext(content: string): ParsedLinearPrompt | null {
  if (!content.trim().startsWith("<issue")) return null;
  const identifier = extractAttribute(content, "issue", "identifier");
  const title = extractInlineTag(content, "title");
  if (!identifier || !title) return null;

  // Linear creates the session on the comment that mentions the agent, so it
  // is the newest comment in the primary directive thread.
  const directiveThread = extractComments(content.match(DIRECTIVE_THREAD_PATTERN)?.[1] ?? "");
  const directive = directiveThread[directiveThread.length - 1];
  const comments = [...directiveThread.slice(0, -1), ...extractComments(content.replace(DIRECTIVE_THREAD_PATTERN, ""))].slice(0, 3);
  const rawDescription = extractMarkdownBlock(content, "description");
  return {
    identifier,
    title,
    description: rawDescription ? normalizeLinearMentions(rawDescription) : undefined,
    teamName: extractAttribute(content, "team", "name"),
    projectName: extractAttribute(content, "project", "name"),
    labels: extractAllInlineTags(content, "label"),
    directive,
    comments
  };
}

/** Roughly how many characters of description we show before collapsing. */
const DESCRIPTION_PREVIEW_CHARS = 320;

/**
 * Truncate a description for the collapsed preview without slicing through
 * markdown image or link syntax. Linear embeds attachments as
 * `![](https://uploads.linear.app/...?signature=…)` markdown, and the signed
 * URLs are routinely 400-600 chars long, well past the 320-char preview
 * threshold. A naive `slice(0, 320)` lands in the middle of the URL, leaves
 * the construct without its closing `)`, and the markdown renderer drops the
 * malformed image entirely, so users see nothing until they click "Show
 * more". When the cut would land inside such a construct we either extend
 * it to include the whole construct (preferred for images so the visual
 * content appears in the preview, capped at ~2x the threshold to keep the
 * card bounded) or back off to just before it.
 */
export function truncateDescription(text: string, maxChars: number): string {
  if (text.length <= maxChars) return text;

  // Matches markdown link `[text](url)` and image `![alt](url)` constructs.
  // The URL portion tolerates anything except `)`, which is fine for Linear
  // URLs and typical web links: they don't contain literal `)` characters.
  const constructPattern = /!?\[[^\]]*\]\([^)]*\)/g;
  let cut = maxChars;
  let match: RegExpExecArray | null;
  while ((match = constructPattern.exec(text)) !== null) {
    const start = match.index;
    const end = start + match[0].length;
    if (start >= cut) break; // Construct starts after the cut, no overlap.
    if (end > cut) {
      const isImage = match[0].startsWith("!");
      // Pull images into the preview when their full markdown stays within
      // roughly 2x the threshold; otherwise drop them so we don't render
      // broken syntax. Links always get dropped on overlap because the
      // visible text would already be partly cut by the time we reach them.
      cut = isImage && end <= maxChars * 2 ? end : start;
    }
  }
  if (cut >= text.length) return text;
  return `${text.slice(0, cut).trimEnd()}\u2026`;
}
interface LinearMessagePreviewProps {
  content: string;
  originUrl?: string;
  className?: string;
}

/** Circular avatar showing a comment author's initials. */
function Initials(t0) {
  const $ = _c(4);
  const {
    name
  } = t0;
  const t1 = name ?? "?";
  let t2;
  if ($[0] !== t1) {
    t2 = t1.split(/\s+/).map(_temp).filter(Boolean).slice(0, 2).join("").toUpperCase();
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const initials = t2;
  let t3;
  if ($[2] !== initials) {
    t3 = <span aria-hidden={true} className="flex size-5 shrink-0 items-center justify-center rounded-full bg-neutral-200 text-[9px] font-semibold text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300">{initials}</span>;
    $[2] = initials;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}

/**
 * Team, labels, description and context comments. Description and comment
 * bodies render as markdown so Linear's headings, code blocks and links
 * survive; long descriptions collapse behind "Show more".
 */
function _temp(word) {
  return word[0];
}
function LinearIssueDetails(t0) {
  const $ = _c(19);
  const {
    parsed
  } = t0;
  const [expanded, setExpanded] = useState(false);
  const description = parsed.description ?? "";
  let t1;
  if ($[0] !== description) {
    t1 = truncateDescription(description, DESCRIPTION_PREVIEW_CHARS);
    $[0] = description;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const truncatedDescription = t1;
  const isLongDescription = truncatedDescription !== description;
  const visibleDescription = expanded ? description : truncatedDescription;
  let t2;
  if ($[2] !== parsed.labels || $[3] !== parsed.projectName || $[4] !== parsed.teamName) {
    t2 = [parsed.teamName, parsed.projectName, ...parsed.labels].filter(Boolean);
    $[2] = parsed.labels;
    $[3] = parsed.projectName;
    $[4] = parsed.teamName;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const metaLine = t2.join(" \xB7 ");
  let t3;
  if ($[6] !== metaLine) {
    t3 = metaLine && <p className="type-body-xs-regular text-neutral-500 dark:text-neutral-400">{metaLine}</p>;
    $[6] = metaLine;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== description || $[9] !== expanded || $[10] !== isLongDescription || $[11] !== visibleDescription) {
    t4 = description && <div><SessionMarkdown content={visibleDescription} />{isLongDescription && <button type="button" onClick={() => setExpanded(_temp2)} className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200">{expanded ? <><ChevronUp className="size-3" aria-hidden={true} />Show less</> : <><ChevronDown className="size-3" aria-hidden={true} />Show more</>}</button>}</div>;
    $[8] = description;
    $[9] = expanded;
    $[10] = isLongDescription;
    $[11] = visibleDescription;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== parsed.comments) {
    t5 = parsed.comments.length > 0 && <div className="pt-1">{parsed.comments.map((comment, index) => <div key={`${comment.author ?? "comment"}-${index}`} className="flex gap-2.5"><div className="flex flex-col items-center"><Initials name={comment.author} />{index < parsed.comments.length - 1 && <span className="mt-1 w-px flex-1 bg-[var(--color-border)]" />}</div><div className={cn("min-w-0 flex-1", index < parsed.comments.length - 1 ? "pb-3" : "pb-0")}>{comment.author && <p className="mb-0.5 type-body-xs-medium text-neutral-600 dark:text-neutral-300">{comment.author}</p>}<SessionMarkdown content={comment.body} /></div></div>)}</div>;
    $[13] = parsed.comments;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t3 || $[16] !== t4 || $[17] !== t5) {
    t6 = <div className="space-y-2">{t3}{t4}{t5}</div>;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
}

/**
 * A comment-triggered session leads with the comment that mentioned the agent
 * and tucks the issue into a collapsible attachment row. A delegated issue has
 * no such comment, so the issue itself is the ask and renders expanded.
 */
function _temp2(prev) {
  return !prev;
}
export function LinearMessagePreview(t0) {
  const $ = _c(29);
  const {
    content,
    originUrl,
    className
  } = t0;
  let t1;
  if ($[0] !== content) {
    t1 = parseLinearPromptContext(content);
    $[0] = content;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const parsed = t1;
  const [issueExpanded, setIssueExpanded] = useState(false);
  if (!parsed) {
    return null;
  }
  const {
    directive
  } = parsed;
  const hasIssueDetails = Boolean(parsed.description || parsed.teamName || parsed.projectName || parsed.labels.length > 0 || parsed.comments.length > 0);
  const canExpandIssue = Boolean(directive) && hasIssueDetails;
  const showIssueDetails = hasIssueDetails && (!directive || issueExpanded);
  let t2;
  if ($[2] !== parsed.title) {
    t2 = <span title={parsed.title} className="min-w-0 truncate type-body-s-medium text-neutral-900 dark:text-neutral-100">{parsed.title}</span>;
    $[2] = parsed.title;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const issueTitle = t2;
  let t3;
  if ($[4] !== className) {
    t3 = cn("min-w-0", className);
    $[4] = className;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== directive) {
    t4 = directive && <div className="mb-3"><div className="flex min-w-0 items-center gap-2"><Initials name={directive.author} />{directive.author && <span className="truncate type-body-xs-medium text-neutral-700 dark:text-neutral-200">{directive.author}</span>}<span className="flex shrink-0 items-center gap-1 type-body-xs-regular text-neutral-500 dark:text-neutral-400">via<SiLinear className="size-3 text-[#5e6ad2]" aria-hidden={true} />Linear comment</span></div><div className="mt-1.5"><SessionMarkdown content={directive.body} /></div></div>;
    $[6] = directive;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <SiLinear className="size-3 shrink-0 text-[#5e6ad2]" aria-hidden={true} />;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== originUrl || $[10] !== parsed.identifier) {
    t6 = originUrl ? <a href={originUrl} target="_blank" rel="noopener noreferrer" className={cn("shrink-0 type-body-xxs-regular font-mono text-neutral-500 dark:text-neutral-400", "rounded underline-offset-2 hover:text-neutral-700 hover:underline focus-visible:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-border)] dark:hover:text-neutral-200")}>{parsed.identifier}</a> : <span className="shrink-0 type-body-xxs-regular font-mono text-neutral-500 dark:text-neutral-400">{parsed.identifier}</span>;
    $[9] = originUrl;
    $[10] = parsed.identifier;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== canExpandIssue || $[13] !== issueExpanded || $[14] !== issueTitle) {
    t7 = canExpandIssue ? <Button type="button" variant="inline" onClick={() => setIssueExpanded(_temp3)} aria-expanded={issueExpanded} className="flex min-w-0 flex-1 items-center justify-start gap-2 whitespace-normal text-left">{issueTitle}<ChevronRight className={cn("ml-auto size-3.5 shrink-0 text-neutral-400 transition-transform dark:text-neutral-500", issueExpanded && "rotate-90")} aria-hidden={true} /></Button> : <div className="flex min-w-0 flex-1">{issueTitle}</div>;
    $[12] = canExpandIssue;
    $[13] = issueExpanded;
    $[14] = issueTitle;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t6 || $[17] !== t7) {
    t8 = <div className="flex items-center gap-2 px-2.5 py-1.5">{t5}{t6}{t7}</div>;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== parsed || $[20] !== showIssueDetails) {
    t9 = showIssueDetails && <div className="border-t border-[var(--color-border)] px-2.5 py-2.5"><LinearIssueDetails parsed={parsed} /></div>;
    $[19] = parsed;
    $[20] = showIssueDetails;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t8 || $[23] !== t9) {
    t10 = <div className="overflow-hidden rounded-md border border-[var(--color-border)] bg-[var(--color-surface)]">{t8}{t9}</div>;
    $[22] = t8;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10 || $[26] !== t3 || $[27] !== t4) {
    t11 = <div className={t3}>{t4}{t10}</div>;
    $[25] = t10;
    $[26] = t3;
    $[27] = t4;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  return t11;
}
function _temp3(prev) {
  return !prev;
}